import { useEffect } from 'react';
import { reportIssue, type IssueKind } from '@/lib/telemetry';

/**
 * The drift detector.
 *
 * The expert stream is not filtered, so unlike its client-dashboard twin this
 * component is not a leak alarm — an expert is meant to see everything. What it
 * catches is a value this build has no mapping for.
 *
 * That still stops a render, for one reason. This renderer's job is to say which
 * turns the client can read and which are hidden. A visibility, an author kind
 * or a block type it does not recognise cannot be drawn either way without
 * asserting something it does not know, and asserting wrongly in either
 * direction is the failure this dashboard exists to prevent: a reviewer who
 * believes their hidden work is public writes a different note, and a reviewer
 * who believes public work is hidden ships something the client never saw.
 *
 * Development gets a loud slab. Production paints a neutral placeholder and
 * reports — never the unrecognised payload itself.
 */
export function UnknownTurn({
  issueKind,
  token,
  context,
}: {
  issueKind: IssueKind;
  /** A short machine token, e.g. the unmapped kind name. Never the payload. */
  token: string;
  context?: Record<string, string | number | boolean | null | undefined>;
}) {
  useEffect(() => {
    reportIssue({ kind: issueKind, token, context });
  }, [issueKind, token, context]);

  if (import.meta.env.DEV) {
    return (
      <div data-drift-marker={issueKind} data-drift-token={token} className="my-2 border-2 border-danger">
        <div className="flex items-center gap-2 bg-danger px-2 py-1 font-mono text-tiny font-semibold uppercase tracking-[0.14em] text-danger-ink">
          <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3 fill-current">
            <path d="M6 0 12 11H0z" />
          </svg>
          Contract drift
        </div>
        <div className="bg-raised px-2.5 py-2 font-mono text-xs text-danger">
          <div>
            <span className="text-ink-3">kind</span> {issueKind}
          </div>
          <div>
            <span className="text-ink-3">token</span> {token}
          </div>
          {context ? (
            <div className="mt-1 text-ink-3">
              {Object.entries(context)
                .map(([k, v]) => `${k}=${String(v)}`)
                .join('  ')}
            </div>
          ) : null}
          <p className="mt-2 max-w-prose whitespace-normal font-sans text-xs leading-[17px] text-ink-2">
            Something arrived that this build has no mapping for. It is not drawn, because drawing it would assert
            whether the client can see it — and this renderer does not know. Regenerate the contract types and add the
            case.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-drift-marker={issueKind}
      className="my-2 border border-dashed border-rule-strong bg-sunken px-2.5 py-2 text-xs text-ink-2"
    >
      This turn could not be rendered, because its visibility could not be established. Nothing has been lost — it has
      been reported.
    </div>
  );
}
